import type { Store } from '@types'
import type { StateCreator, StoreApi } from 'zustand'
import {
  isHistoryRecordingSuppressed,
  popHistorySuppress,
  pushHistorySuppress,
} from './historyRecordingState'
import { captureHistorySnapshot, deserializeHistorySnapshot } from './snapshot'
import { hasSceneHistoryDiff } from './shouldRecordHistoryChange'
import type { HistoryActions } from './types'
import {
  clearHistoryEngine,
  commitHistoryBatchSnapshot,
  isHistoryApplyInProgress,
  pauseHistoryEngine,
  recordDestructiveHistoryChange,
  redoHistory,
  resumeHistoryEngine,
  undoHistory,
} from './historyEngine'
import { pushHistoryPastSnapshot } from './historyStack'

type HistoryMiddlewareOptions = {
  limit?: number
}

type HistoryStateCreator<T> = StateCreator<T & HistoryActions, [], [], T & HistoryActions>

type HistoryActionsWithBatch = HistoryActions & {
  commitHistoryBatch: (beforeSerialized: string) => void
}

/**
 * Middleware de historial: grabación solo en operaciones explícitas (batch, drag, delete).
 */
export function historyMiddleware<T extends Store>(
  config: StateCreator<T, [], [], T>,
  _options: HistoryMiddlewareOptions = {},
): HistoryStateCreator<T> {
  return (set, get, api) => {
    const recordPausedChange = (beforeSerialized: string): void => {
      const before = deserializeHistorySnapshot(beforeSerialized)
      const after = captureHistorySnapshot(get() as Store)
      if (!hasSceneHistoryDiff(before, after)) return
      pushHistoryPastSnapshot(beforeSerialized)
    }

    const wrappedSet: typeof set = (partial, replace) => {
      if (isHistoryApplyInProgress() || isHistoryRecordingSuppressed()) {
        set(partial, replace)
        return
      }
      set(partial, replace)
    }

    const baseState = config(
      wrappedSet as unknown as Parameters<typeof config>[0],
      get as () => T,
      api as StoreApi<T>,
    )

    const historyActions: HistoryActionsWithBatch = {
      commitHistoryBatch: commitHistoryBatchSnapshot,
      undo: undoHistory,
      redo: redoHistory,
      pauseHistory: pauseHistoryEngine,
      resumeHistory: () => {
        resumeHistoryEngine(recordPausedChange)
      },
      clearHistory: clearHistoryEngine,
      recordDestructiveChange: (runChange: () => void) => {
        pushHistorySuppress()
        try {
          recordDestructiveHistoryChange(runChange)
        } finally {
          popHistorySuppress()
        }
      },
    }

    return {
      ...baseState,
      ...historyActions,
    }
  }
}
